import React, { useEffect, useState } from 'react';
import { Alert, Button, Empty, Grid, Skeleton, Table, Tabs } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { accountPointsApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import OrderDetail from '../components/OrderDetail';

const PointsManagement = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState('usage');
  const [revision, setRevision] = useState(0);
  const [balance, setBalance] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(true);
  const [balanceError, setBalanceError] = useState('');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === false;
  useEffect(() => {
    let current = true;
    setBalanceLoading(true);
    setBalanceError('');
    accountPointsApi
      .getAccountPoints(user?.username)
      .then((response) => {
        if (
          response.data?.code !== 200 ||
          response.data.data?.pointsBalance == null
        )
          throw new Error(response.data?.message || '积分余额暂不可用');
        if (current) setBalance(response.data.data.pointsBalance);
      })
      .catch((failure) => {
        if (current) {
          setBalance(null);
          setBalanceError(failure.message);
        }
      })
      .finally(() => {
        if (current) setBalanceLoading(false);
      });
    return () => {
      current = false;
    };
  }, [revision, user?.username]);
  useEffect(() => {
    let current = true;
    setLoading(true);
    setError('');
    const fetchLedger =
      tab === 'usage'
        ? accountPointsApi.getPointUsageDetails
        : accountPointsApi.getPointRechargeDetails;
    fetchLedger({ username: user?.username, page, size: pageSize })
      .then((response) => {
        if (
          response.data?.code !== 200 ||
          !Array.isArray(response.data.data?.records)
        )
          throw new Error(response.data?.message || '积分明细暂不可用');
        if (current) {
          setRows(response.data.data.records);
          setTotal(response.data.data.total || 0);
        }
      })
      .catch((failure) => {
        if (current) setError(failure.message);
      })
      .finally(() => {
        if (current) setLoading(false);
      });
    return () => {
      current = false;
    };
  }, [tab, page, pageSize, revision, user?.username]);
  const formatTime = (value) =>
    value ? new Date(value).toLocaleString('zh-CN', { hour12: false }) : '-';
  const renderChange = (value) => (
    <span
      className="numeric"
      style={{ color: tab === 'usage' ? 'var(--ink)' : '#39745b' }}
    >
      {value == null
        ? '-'
        : (tab === 'usage' ? '−' : '+') + Math.abs(value).toLocaleString()}
    </span>
  );
  const renderOrder = (value) =>
    value == null || value === '' ? (
      '-'
    ) : (
      <Button
        type="link"
        size="small"
        className="ledger-order-link"
        aria-label={'查看关联订单 ' + value}
        onClick={() => setSelectedOrder({ id: value })}
      >
        订单 {value}
      </Button>
    );
  const columns = isMobile
    ? [
        {
          title: '记录',
          key: 'record',
          render: (_, record) => (
            <div className="ledger-record">
              <span>
                {record.sourceDescription ||
                  (tab === 'usage' ? '积分使用' : '积分充值')}
              </span>
              <small>{formatTime(record.createTime)}</small>
              {tab === 'usage' &&
                record.relatedOrderId != null &&
                renderOrder(record.relatedOrderId)}
            </div>
          ),
        },
        {
          title: '积分变更',
          dataIndex: 'pointsChange',
          align: 'right',
          width: 124,
          render: (value, record) => (
            <div className="ledger-record">
              {renderChange(value)}
              <small>
                变更后余额{' '}
                {record.balanceAfterChange == null
                  ? '-'
                  : Number(record.balanceAfterChange).toLocaleString()}
              </small>
            </div>
          ),
        },
      ]
    : [
        {
          title: '变更时间',
          dataIndex: 'createTime',
          width: 190,
          render: formatTime,
        },
        {
          title: '积分变更',
          dataIndex: 'pointsChange',
          width: 130,
          align: 'right',
          render: renderChange,
        },
        {
          title: '变更后余额',
          dataIndex: 'balanceAfterChange',
          width: 140,
          align: 'right',
          render: (value) => (
            <span className="numeric">
              {value == null ? '-' : Number(value).toLocaleString()}
            </span>
          ),
        },
        { title: '来源说明', dataIndex: 'sourceDescription', width: 260 },
        ...(tab === 'usage'
          ? [
              {
                title: '关联订单 ID',
                dataIndex: 'relatedOrderId',
                width: 140,
                render: renderOrder,
              },
            ]
          : []),
      ];
  return (
    <section className="page-container">
      <header className="page-header">
        <div>
          <h1 className="page-title">账户积分</h1>
          <p className="page-description">
            查看可用余额，核对每次使用与充值记录。
          </p>
        </div>
        <Button
          icon={<ReloadOutlined />}
          loading={loading || balanceLoading}
          onClick={() => setRevision((value) => value + 1)}
        >
          刷新
        </Button>
      </header>
      <div className="page-content">
        <div className="points-overview">
          <div>
            <p className="muted">当前可用积分</p>
            {balanceLoading ? (
              <Skeleton.Input active={false} />
            ) : (
              <div className="points-balance">
                {balance == null ? '暂不可用' : balance.toLocaleString()}
              </div>
            )}
          </div>
          <details className="points-rules">
            <summary>计费与充值说明</summary>
            <p>
              每个待审核订单转为待拍单，消耗 1
              积分。批量处理前会显示可处理数量和预计消耗。
            </p>
            <p>
              需要充值或对账协助，请联系业务对接人。记录按发生时间查询，可结合关联订单核对。
            </p>
          </details>
        </div>
        {balanceError && (
          <Alert
            className="inline-error"
            showIcon
            type="error"
            message={balanceError}
          />
        )}
        <Tabs
          activeKey={tab}
          items={[
            { key: 'usage', label: '使用明细' },
            { key: 'recharge', label: '充值明细' },
          ]}
          onChange={(key) => {
            setTab(key);
            setPage(1);
            setRows([]);
            setTotal(0);
          }}
        />
        {error && (
          <Alert
            className="inline-error"
            showIcon
            type="error"
            message={error}
            description="下方可能保留上次加载的记录，请刷新后核对。"
            action={
              <Button
                size="small"
                onClick={() => setRevision((value) => value + 1)}
              >
                重试
              </Button>
            }
          />
        )}
        <Table
          rowKey="id"
          size="small"
          columns={columns}
          dataSource={rows}
          loading={loading}
          scroll={isMobile ? undefined : { x: 760 }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            showTotal: (count) => '共 ' + count + ' 条记录',
          }}
          onChange={(pagination) => {
            setPage(pagination.pageSize !== pageSize ? 1 : pagination.current);
            setPageSize(pagination.pageSize);
            setRows([]);
          }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  error
                    ? '积分明细加载失败'
                    : tab === 'usage'
                      ? '暂无积分使用记录'
                      : '暂无积分充值记录'
                }
              />
            ),
          }}
        />
      </div>
      <OrderDetail
        visible={Boolean(selectedOrder)}
        order={selectedOrder}
        onClose={() => setSelectedOrder(null)}
      />
    </section>
  );
};
export default PointsManagement;
